import { match } from 'ts-pattern';

import { TeamType } from 'src/constants/graphql-enums';

type ProductType = 'industry_stream';

// Which actions the user can perform
export type ProductAction =
  | 'create_study'
  | 'view_studies'
  | 'view_askable_plus'
  | 'view_notifications'
  | 'view_ai_chat'
  | 'view_docs';

type ProductPermissions = {
  routes: string[];
  actions: ProductAction[];
  defaultRoute: string;
};

type ProductAccessOptions = {
  streamsUiEnabled?: boolean;
};

const DEFAULT_ROUTE = '/studies';

// Permissions for industry_stream teams when industry_streams_ui is OFF — the live prod
// experience today (/chat, /docs, /highlights, etc.). Will be retired in favour of
// STREAMS_UI_PERMISSIONS once the flag goes 100%.
const INDUSTRY_STREAM_PERMISSIONS: ProductPermissions = {
  routes: ['/dashboard', '/chat', '/docs', '/highlights', '/settings/account', '/settings/members', '/teams'],
  actions: ['view_ai_chat', 'view_docs'],
  defaultRoute: '/chat',
};

// Permissions for industry_stream teams when industry_streams_ui is ON. Collapses the
// allowed route set to the new /streams shell plus /teams (still served by the legacy
// app shell for now) — existing /chat, /docs, /settings routes are intentionally
// unreachable while the flag is on.
const STREAMS_UI_PERMISSIONS: ProductPermissions = {
  routes: ['/streams', '/teams'],
  actions: ['view_ai_chat', 'view_docs'],
  defaultRoute: '/streams',
};

const resolvePermissions = (
  activeProduct: ProductType | null,
  options?: ProductAccessOptions,
): ProductPermissions | null => {
  if (activeProduct !== 'industry_stream') return null;
  return options?.streamsUiEnabled ? STREAMS_UI_PERMISSIONS : INDUSTRY_STREAM_PERMISSIONS;
};

export const getActiveProduct = (type?: TeamType | (string & {}) | null): ProductType | null => {
  return match(type)
    .with(TeamType.IndustryStreamsOnly, () => 'industry_stream' as const)
    .otherwise(() => null);
};

export const getAllowedRoutes = (
  activeProduct: ProductType | null,
  options?: ProductAccessOptions,
): string[] | null => {
  return resolvePermissions(activeProduct, options)?.routes ?? null;
};

// Matches both exact and child routes (e.g., '/chat' matches '/chat' and '/chat/123')
export const isRouteAllowed = (pathname: string, allowedRoutes: string[] | null): boolean => {
  return match(allowedRoutes)
    .with(null, () => true)
    .otherwise(routes => routes.some(route => pathname === route || pathname.startsWith(`${route}/`)));
};

export const getAllowedActions = (
  activeProduct: ProductType | null,
  options?: ProductAccessOptions,
): ProductAction[] | null => {
  return resolvePermissions(activeProduct, options)?.actions ?? null;
};

export const isActionAllowed = (action: ProductAction, allowedActions: ProductAction[] | null): boolean => {
  return match(allowedActions)
    .with(null, () => true)
    .otherwise(actions => actions.includes(action));
};

export const getDefaultRoute = (activeProduct: ProductType | null, options?: ProductAccessOptions): string => {
  return resolvePermissions(activeProduct, options)?.defaultRoute ?? DEFAULT_ROUTE;
};
